import React, { useCallback, useRef, useState } from "react";
import Resources from "../types/Resources";
import CounterSignatureRequest, {
    CounterSignatureRequest_departmentIsRequested,
    CounterSignatureRequest_companyIsRequested,
    CounterSignatureRequest_userIsRequested
} from "../types/CounterSignatureRequest";
import { debounce, showDangerDialog } from "../common";
import ProblemDetails from "../types/responses/ProblemDetails";
import Result from "../types/Result";
import CounterSignatureRequestRecipient from "../types/CounterSignatureRequestRecipient";
import { searchCounterSignatureRequestRecipients } from "../apiClient";

export type CounterSignatureRequestInputManager = {
    searchInput: string;
    counterSignatureRequest: CounterSignatureRequest;
    canCreateCounterSignatureRequest: boolean;
    canCancelCounterSignatureRequest: boolean;
    disableSendButton: boolean;
    disableCancelButton: boolean;
    disableInput: boolean;
    hasActiveCounterSignatureRequest: boolean;
    activeRecipientText: string;
    showResultsDropdown: boolean;
    searchResultRecipients: CounterSignatureRequestRecipient[];
    selectedRecipient: CounterSignatureRequestRecipient | undefined;
    showCounterSignatureRequestSection: boolean;

    sendButtonClickHandlerAsync: () => Promise<void>;
    cancelButtonClickHandlerAsync: () => Promise<void>;

    searchResultListItemClickHandler: (recipient: CounterSignatureRequestRecipient) => void;
    unselectRecipientHandler: () => void;
    closeResultsDropdownHandler: () => void;
    searchInputChangeHandler: (updatedInput: string) => void;
    searchResultListItemKeyDownHandler: (e: React.KeyboardEvent, recipient: CounterSignatureRequestRecipient) => void;
};

type UseCounterSignatureRequestHandlerProps = {
    resources: Resources;
    counterSignatureRequest: CounterSignatureRequest;
    showCounterSignatureRequestSection: boolean;
    hasUnsavedChanges?: boolean;

    onSendCounterSignatureRequestAsync: (recipient: CounterSignatureRequestRecipient) => Promise<void>;
    onCancelCounterSignatureRequestAsync: () => Promise<void>;
};

export default function useCounterSignatureRequestInput(
    props: UseCounterSignatureRequestHandlerProps
): CounterSignatureRequestInputManager {
    validateProps(props);

    const { counterSignatureRequest } = props;

    const [searchInput, setSearchInput] = useState("");
    const [selectedRecipient, setSelectedRecipient] = useState<CounterSignatureRequestRecipient>();
    const [searchResultRecipients, setSearchResultRecipients] = useState<CounterSignatureRequestRecipient[]>([]);
    const [showResultsDropdown, setShowResultsDropdown] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const debounceSearch = useCallback(
        debounce(async (input: string) => {
            const searchResult: Result<CounterSignatureRequestRecipient[], ProblemDetails> =
                await searchCounterSignatureRequestRecipients(input);

            if (searchResult.failed) {
                setShowResultsDropdown(false);
                setSearchResultRecipients([]);

                console.error(searchResult.error);
                showDangerDialog(
                    props.resources.get("SomethingWentWrong"),
                    props.resources.get("RefreshPageAndTryAgain")
                );

                return;
            }

            setShowResultsDropdown(true);
            setSearchResultRecipients(searchResult.value);
        }, 400),
        []
    );

    function closeResultsDropdownHandler() {
        setShowResultsDropdown(false);
    }

    function searchInputChangeHandler(updatedInput: string) {
        setSearchInput(updatedInput);
        setSelectedRecipient(undefined);

        if (!updatedInput) {
            setShowResultsDropdown(false);
            setSearchResultRecipients([]);
            return;
        }

        debounceSearch(updatedInput);
    }

    function searchResultListItemClickHandler(recipient: CounterSignatureRequestRecipient) {
        setSelectedRecipient(recipient);
        setShowResultsDropdown(false);
    }

    function unselectRecipientHandler() {
        setSelectedRecipient(undefined);
        setSearchInput("");
    }

    async function sendButtonClickHandlerAsync() {
        if (!selectedRecipient) {
            console.error("No recipient selected.");
            return;
        }

        setIsLoading(true);
        await props.onSendCounterSignatureRequestAsync(selectedRecipient);
        setIsLoading(false);

        setSelectedRecipient(undefined);
        setSearchInput("");
    }

    async function cancelButtonClickHandlerAsync() {
        await props.onCancelCounterSignatureRequestAsync();
    }

    async function searchResultListItemKeyDownHandler(
        e: React.KeyboardEvent,
        recipient: CounterSignatureRequestRecipient
    ) {
        if (e.key === "Enter" || e.key === " ") {
            setSelectedRecipient(recipient);
            setShowResultsDropdown(false);
            e.preventDefault();
        }
    }

    const activeRecipientText = CounterSignatureRequest_userIsRequested(counterSignatureRequest)
        ? counterSignatureRequest.requestedUserEmailSnapshot
        : CounterSignatureRequest_departmentIsRequested(counterSignatureRequest)
          ? `${counterSignatureRequest.requestedCompanyNameSnapshot}, ${counterSignatureRequest.requestedCompanyDepartmentNameSnapshot}`
          : CounterSignatureRequest_companyIsRequested(counterSignatureRequest)
            ? counterSignatureRequest.requestedCompanyNameSnapshot
            : "";

    const hasActiveCounterSignatureRequest = !!counterSignatureRequest.id;
    const canCreateCounterSignatureRequest = !hasActiveCounterSignatureRequest && counterSignatureRequest.canManage;
    const canCancelCounterSignatureRequest = hasActiveCounterSignatureRequest && counterSignatureRequest.canManage;
    const disableSendButton = !selectedRecipient || isLoading || !!props.hasUnsavedChanges;
    const disableCancelButton = isLoading;
    const disableInput = !!hasActiveCounterSignatureRequest || !!props.hasUnsavedChanges;

    return {
        searchInput,
        canCreateCounterSignatureRequest,
        canCancelCounterSignatureRequest,
        counterSignatureRequest,
        disableInput,
        hasActiveCounterSignatureRequest,
        activeRecipientText,
        showResultsDropdown,
        searchResultRecipients,
        selectedRecipient,
        disableSendButton,
        disableCancelButton,
        showCounterSignatureRequestSection: props.showCounterSignatureRequestSection,

        searchInputChangeHandler,
        closeResultsDropdownHandler,
        searchResultListItemClickHandler,
        unselectRecipientHandler,
        searchResultListItemKeyDownHandler,

        sendButtonClickHandlerAsync,
        cancelButtonClickHandlerAsync
    };
}

function validateProps(props: UseCounterSignatureRequestHandlerProps) {
    if (!props.resources) {
        throw new Error("Resources are required.");
    }

    if (!props.counterSignatureRequest) {
        throw new Error("CounterSignatureRequest is required.");
    }
}
